import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Settings, 
  Phone, 
  MessageSquare, 
  Clock, 
  Users, 
  RefreshCw,
  Save,
  AlertCircle,
  CheckCircle,
  XCircle,
  PhoneCall,
  PhoneOff,
  Calendar,
  Zap
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AgentAssignmentOrchestrationSettings() {
  const [settings, setSettings] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [stats, setStats] = useState(null);
  
  const token = sessionStorage.getItem('auth_token');

  useEffect(() => {
    if (token) {
      fetchSettings();
      fetchStats();
    }
  }, [token]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/assignment-orchestrator/settings?token=${token}`);
      if (response.data.success) {
        setSettings(response.data.settings);
        setCurrentStatus(response.data.current_status);
      }
    } catch (error) {
      console.error('Failed to fetch settings', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/api/assignment-orchestrator/stats?token=${token}`);
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats', error);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      const response = await axios.put(
        `${API}/api/assignment-orchestrator/settings?token=${token}`,
        settings
      );
      if (response.data.success) {
        setSettings(response.data.settings);
        toast.success('Settings saved successfully');
      }
    } catch (error) {
      console.error('Failed to save settings', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const toggleRole = (role) => {
    const currentRoles = settings?.apply_to_roles || [];
    if (currentRoles.includes(role)) {
      updateSetting('apply_to_roles', currentRoles.filter(r => r !== role));
    } else {
      updateSetting('apply_to_roles', [...currentRoles, role]);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agent Assignment Orchestration</h1>
          <p className="text-gray-500 mt-1">
            Configure pre-initiation WhatsApp and call flow when leads are assigned to agents
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* Current Status Badge */}
          {currentStatus && (
            <div className="flex items-center gap-2">
              <Badge variant={currentStatus.within_working_hours ? "default" : "secondary"}>
                <Clock className="h-3 w-3 mr-1" />
                {currentStatus.current_time_uae} UAE
              </Badge>
              <Badge variant={currentStatus.within_working_hours ? "default" : "outline"}>
                {currentStatus.within_working_hours ? 'Working Hours' : 'After Hours'}
              </Badge>
            </div>
          )}
          <Button onClick={saveSettings} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      {/* Master Toggle */}
      <Card className={settings?.enabled ? 'border-green-200 bg-green-50' : 'border-gray-200'}>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg ${settings?.enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                <Zap className={`h-6 w-6 ${settings?.enabled ? 'text-green-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <h3 className="font-semibold text-lg">
                  Enable Assignment Pre-Initiation Flow
                </h3>
                <p className="text-sm text-gray-500">
                  When enabled, system will send WhatsApp and orchestrate calls on agent assignment
                </p>
              </div>
            </div>
            <Switch
              checked={settings?.enabled || false}
              onCheckedChange={(checked) => updateSetting('enabled', checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="general" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            General
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="call" className="flex items-center gap-2">
            <Phone className="h-4 w-4" />
            Call Flow
          </TabsTrigger>
          <TabsTrigger value="fallback" className="flex items-center gap-2">
            <PhoneOff className="h-4 w-4" />
            Fallback
          </TabsTrigger>
          <TabsTrigger value="stats" className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Stats
          </TabsTrigger>
        </TabsList>

        {/* General Settings Tab */}
        <TabsContent value="general" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Apply To Roles</CardTitle>
              <CardDescription>
                Select which agent roles should trigger the orchestration flow
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-3">
                {['sales_agent', 'telesales', 'sales_manager'].map((role) => (
                  <div
                    key={role}
                    onClick={() => toggleRole(role)}
                    className={`px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                      settings?.apply_to_roles?.includes(role)
                        ? 'bg-blue-50 border-blue-300 text-blue-700'
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      {role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Working Hours (UAE Time)</CardTitle>
              <CardDescription>
                Orchestration will only initiate live calls during these hours.
                Outside these hours, after-hours flow will be triggered.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <Input
                    type="time"
                    value={settings?.working_hours_start || '08:00'}
                    onChange={(e) => updateSetting('working_hours_start', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Time</Label>
                  <Input
                    type="time"
                    value={settings?.working_hours_end || '22:00'}
                    onChange={(e) => updateSetting('working_hours_end', e.target.value)}
                  />
                </div>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  <Clock className="h-4 w-4 inline mr-1" />
                  Timezone: <strong>{settings?.timezone || 'Asia/Dubai'}</strong>
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Confirmation Settings</CardTitle>
              <CardDescription>
                Configure how leads confirm before call connection
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="font-medium">Require Lead Confirmation Before Call</Label>
                  <p className="text-sm text-gray-500">
                    Wait for lead to reply "YES" before initiating call
                  </p>
                </div>
                <Switch
                  checked={settings?.require_lead_confirmation || false}
                  onCheckedChange={(checked) => updateSetting('require_lead_confirmation', checked)}
                />
              </div>

              <div className="space-y-2">
                <Label>Confirmation Timeout (minutes)</Label>
                <Input
                  type="number"
                  min="1"
                  max="60"
                  value={settings?.confirmation_timeout_minutes || 5}
                  onChange={(e) => updateSetting('confirmation_timeout_minutes', parseInt(e.target.value))}
                />
                <p className="text-xs text-gray-500">
                  Time to wait for lead response before timeout action
                </p>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="font-medium">Auto-Attempt Call on No Reply</Label>
                  <p className="text-sm text-gray-500">
                    If lead doesn't respond within timeout, attempt call anyway
                  </p>
                </div>
                <Switch
                  checked={settings?.auto_attempt_call_on_no_reply || false}
                  onCheckedChange={(checked) => updateSetting('auto_attempt_call_on_no_reply', checked)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Pre-Initiation WhatsApp Template</CardTitle>
              <CardDescription>
                Sent to lead during working hours when assigned to an agent.
                Variables: {'{{lead_name}}'}, {'{{agent_name}}'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                rows={5}
                value={settings?.pre_initiation_template || ''}
                onChange={(e) => updateSetting('pre_initiation_template', e.target.value)}
                placeholder="Hello {{lead_name}}, Our sales representative {{agent_name}} is ready to connect with you..."
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>After-Hours WhatsApp Template</CardTitle>
              <CardDescription>
                Sent to lead outside working hours.
                Variables: {'{{lead_name}}'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                rows={5}
                value={settings?.after_hours_template || ''}
                onChange={(e) => updateSetting('after_hours_template', e.target.value)}
                placeholder="Hello {{lead_name}}, Our team will connect with you during working hours..."
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>No Answer WhatsApp Template</CardTitle>
              <CardDescription>
                Sent when lead doesn't answer the call.
                Variables: {'{{lead_name}}'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                rows={3}
                value={settings?.no_answer_whatsapp_template || ''}
                onChange={(e) => updateSetting('no_answer_whatsapp_template', e.target.value)}
                placeholder="We tried to reach you but couldn't connect..."
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Call Failed WhatsApp Template</CardTitle>
              <CardDescription>
                Sent when call connection fails (agent unavailable, etc.).
                Variables: {'{{lead_name}}'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                rows={3}
                value={settings?.call_failed_whatsapp_template || ''}
                onChange={(e) => updateSetting('call_failed_whatsapp_template', e.target.value)}
                placeholder="We experienced a technical issue connecting your call..."
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Call Flow Tab */}
        <TabsContent value="call" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Call Announcements</CardTitle>
              <CardDescription>
                Messages played when calls are connected
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Lead Announcement</Label>
                <Input
                  value={settings?.announcement_message || ''}
                  onChange={(e) => updateSetting('announcement_message', e.target.value)}
                  placeholder="Please hold while we connect you with our representative."
                />
                <p className="text-xs text-gray-500">
                  Message played to lead when they answer
                </p>
              </div>

              <div className="space-y-2">
                <Label>Agent Announcement</Label>
                <Input
                  value={settings?.agent_announcement_message || ''}
                  onChange={(e) => updateSetting('agent_announcement_message', e.target.value)}
                  placeholder="Connecting you with a lead who confirmed interest."
                />
                <p className="text-xs text-gray-500">
                  Message played to agent when they answer
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Retry Settings</CardTitle>
              <CardDescription>
                Configure call retry behavior
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Lead Call Retry Count</Label>
                  <Input
                    type="number"
                    min="1"
                    max="5"
                    value={settings?.retry_lead_call_count || 2}
                    onChange={(e) => updateSetting('retry_lead_call_count', parseInt(e.target.value))}
                  />
                  <p className="text-xs text-gray-500">
                    Number of attempts to reach the lead
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Agent Call Retry Count</Label>
                  <Input
                    type="number"
                    min="1"
                    max="5"
                    value={settings?.retry_agent_call_count || 2}
                    onChange={(e) => updateSetting('retry_agent_call_count', parseInt(e.target.value))}
                  />
                  <p className="text-xs text-gray-500">
                    Number of attempts to reach the agent
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Call Flow Diagram */}
          <Card className="bg-gray-50">
            <CardHeader>
              <CardTitle>Call Flow (Lead-First)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between text-sm">
                <div className="flex flex-col items-center p-4 bg-white rounded-lg border">
                  <MessageSquare className="h-8 w-8 text-green-500 mb-2" />
                  <span className="font-medium">1. WhatsApp Sent</span>
                  <span className="text-xs text-gray-500">Pre-initiation</span>
                </div>
                <div className="text-gray-400">→</div>
                <div className="flex flex-col items-center p-4 bg-white rounded-lg border">
                  <CheckCircle className="h-8 w-8 text-blue-500 mb-2" />
                  <span className="font-medium">2. Lead Confirms</span>
                  <span className="text-xs text-gray-500">Reply "YES"</span>
                </div>
                <div className="text-gray-400">→</div>
                <div className="flex flex-col items-center p-4 bg-white rounded-lg border">
                  <Phone className="h-8 w-8 text-purple-500 mb-2" />
                  <span className="font-medium">3. Call Lead</span>
                  <span className="text-xs text-gray-500">First party</span>
                </div>
                <div className="text-gray-400">→</div>
                <div className="flex flex-col items-center p-4 bg-white rounded-lg border">
                  <PhoneCall className="h-8 w-8 text-orange-500 mb-2" />
                  <span className="font-medium">4. Call Agent</span>
                  <span className="text-xs text-gray-500">Second party</span>
                </div>
                <div className="text-gray-400">→</div>
                <div className="flex flex-col items-center p-4 bg-white rounded-lg border">
                  <Users className="h-8 w-8 text-green-600 mb-2" />
                  <span className="font-medium">5. Bridge</span>
                  <span className="text-xs text-gray-500">Connected</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fallback Tab */}
        <TabsContent value="fallback" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Fallback Agent</CardTitle>
              <CardDescription>
                Configure a backup agent when primary agent is unavailable
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="font-medium">Enable Fallback Agent</Label>
                  <p className="text-sm text-gray-500">
                    Try backup agent if primary agent doesn't answer
                  </p>
                </div>
                <Switch
                  checked={settings?.enable_fallback_agent || false}
                  onCheckedChange={(checked) => updateSetting('enable_fallback_agent', checked)}
                />
              </div>

              {settings?.enable_fallback_agent && (
                <div className="space-y-2">
                  <Label>Fallback Agent ID</Label>
                  <Input
                    value={settings?.fallback_agent_id || ''}
                    onChange={(e) => updateSetting('fallback_agent_id', e.target.value)}
                    placeholder="Enter user ID of fallback agent"
                  />
                  <p className="text-xs text-gray-500">
                    Agent must have phone number configured in User Management
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>After-Hours Scheduling</CardTitle>
              <CardDescription>
                Configure behavior when assignment happens outside working hours
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="font-medium">Enable After-Hours Scheduling</Label>
                  <p className="text-sm text-gray-500">
                    Allow leads to schedule callbacks via WhatsApp response
                  </p>
                </div>
                <Switch
                  checked={settings?.enable_after_hours_scheduling || false}
                  onCheckedChange={(checked) => updateSetting('enable_after_hours_scheduling', checked)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Edge Cases Info */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900">Edge Case Handling</p>
                  <ul className="text-sm text-blue-700 mt-2 space-y-1">
                    <li>• <strong>Lead no answer:</strong> Retry calls, then send WhatsApp fallback</li>
                    <li>• <strong>Agent no answer:</strong> Try fallback agent if enabled, else end gracefully</li>
                    <li>• <strong>Lead answered, agent unavailable:</strong> Play message and end call cleanly</li>
                    <li>• <strong>Call dropped:</strong> Send WhatsApp with scheduling option</li>
                    <li>• <strong>Missing agent phone:</strong> Log error, preserve system stability</li>
                    <li>• <strong>Outside working hours:</strong> Send after-hours WhatsApp only</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stats Tab */}
        <TabsContent value="stats" className="space-y-4">
          <div className="grid grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-gray-900">{stats?.total || 0}</p>
                  <p className="text-sm text-gray-500">Total Orchestrations</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-green-600">{stats?.by_state?.call_bridged || 0}</p>
                  <p className="text-sm text-gray-500">Successfully Connected</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-yellow-600">{stats?.by_state?.whatsapp_sent || 0}</p>
                  <p className="text-sm text-gray-500">Awaiting Response</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-red-600">{stats?.by_state?.failed || 0}</p>
                  <p className="text-sm text-gray-500">Failed</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>State Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {stats?.by_state && Object.entries(stats.by_state).map(([state, count]) => (
                  <div key={state} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="font-medium capitalize">{state.replace('_', ' ')}</span>
                    <Badge variant="secondary">{count}</Badge>
                  </div>
                ))}
                {(!stats?.by_state || Object.keys(stats.by_state).length === 0) && (
                  <p className="text-center text-gray-500 py-4">No orchestrations yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Success Rate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="flex-1 bg-gray-200 rounded-full h-4">
                  <div
                    className="bg-green-500 h-4 rounded-full transition-all"
                    style={{ width: `${stats?.success_rate || 0}%` }}
                  />
                </div>
                <span className="font-bold text-lg">{(stats?.success_rate || 0).toFixed(1)}%</span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
